import { API } from "aws-amplify";
import { OPERATION_MAPPINGS } from "./operation-mappings";
import {
  HttpMethod,
  simplApiRequest,
  SimplApiHttpError
} from "./simpl-api-client";

/**
 * Routes the last remaining `API.graphql(...)` call sites (via the helpers in
 * `features/api/apiUtils.ts`) through REST, plus monkey-patches Amplify's
 * `API.get/post/put/del/patch` so every call site uses `simplApiRequest`
 * under the hood. That means:
 *
 *   - Cross-origin requests now carry `credentials: "include"` (needed for
 *     the HttpOnly `SIMPL_AUTH_JWT_<env>` cookie after the bootstrap bridge).
 *   - The Cognito ID token travels as `Authorization: Bearer …` during the
 *     dual-issuer rollout, exactly the way Amplify's old `custom_header`
 *     produced it.
 *   - Every request picks up the `X-Client-*` metadata headers automatically.
 *
 * The AppSync GraphQL endpoint has been removed from simpl-api; an unmapped
 * operation now throws loudly instead of falling back to Amplify's native
 * graphql (which would fail with a confusing "no graphqlEndpoint" error).
 */

function extractOperationName(query: string): string {
  const match = query
    ?.toString()
    ?.match(/(?:query|mutation|subscription)\s+(\w+)/);
  return match ? match[1] : "";
}

(API as any).graphql = async (options: any) => {
  const operationName = extractOperationName(options?.query);
  const mapping = OPERATION_MAPPINGS[operationName];

  if (!mapping) {
    throw new Error(
      `No REST mapping for GraphQL operation "${
        operationName || "<unnamed>"
      }". Add it to OPERATION_MAPPINGS or migrate the call site to fetch / API.get.`
    );
  }

  const url = mapping.urlBuilder
    ? mapping.urlBuilder(options?.variables || {})
    : mapping.restEndpoint;

  try {
    const sendsBody = mapping.method !== "GET" && options?.variables;
    const restData = await simplApiRequest<any>(mapping.method, url, {
      body: sendsBody ? options.variables : undefined
    });
    return mapping.transformer(restData, options?.variables);
  } catch (err) {
    if (
      err instanceof SimplApiHttpError &&
      err.status === 404 &&
      typeof mapping.handleNotFound === "function"
    ) {
      return mapping.handleNotFound(options?.variables);
    }
    throw err;
  }
};

type AmplifyRestInit = {
  body?: unknown;
  queryStringParameters?: Record<string, any>;
  headers?: Record<string, string>;
};

function restMethod(method: HttpMethod) {
  return async (
    _apiName: string,
    path: string,
    init?: AmplifyRestInit
  ): Promise<any> => {
    return simplApiRequest<any>(method, path, {
      body: init?.body,
      queryStringParameters: init?.queryStringParameters,
      headers: init?.headers
    });
  };
}

(API as any).get = restMethod("GET");
(API as any).post = restMethod("POST");
(API as any).put = restMethod("PUT");
(API as any).del = restMethod("DELETE");
(API as any).patch = restMethod("PATCH");

export { extractOperationName };
